import { faStar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ReactNode } from 'react';

import Tooltip, { type TooltipProps } from './Tooltip';

const VISIBLE_DAYS = 45;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

type NewBadgeVariant = 'success' | 'secondary';

export interface NewBadgeProps {
    date: Date | string | number;
    text: ReactNode;
    placement?: TooltipProps['placement'];
    variant?: NewBadgeVariant;
}

/**
 * CSS custom properties per variant:
 * --nb-glow   – base ring/halo color
 * --nb-bright – highlight that runs around the rotating border
 * --nb-soft   – soft outer halo color
 */
const variantConfig: Record<NewBadgeVariant, { vars: string; inner: string }> = {
    success: {
        vars: '[--nb-glow:#38c172] [--nb-bright:#a7f3c9] [--nb-soft:rgba(56,193,114,0.6)]',
        inner: 'tw-bg-success tw-text-white'
    },
    secondary: {
        vars: '[--nb-glow:#889096] [--nb-bright:#d7dbdf] [--nb-soft:rgba(136,144,150,0.55)]',
        inner: 'tw-bg-accents-8 tw-text-white'
    }
};

function isVisible(date: NewBadgeProps['date']) {
    const addedAt = new Date(date).getTime();

    if (Number.isNaN(addedAt)) {
        return false;
    }

    return Date.now() - addedAt <= VISIBLE_DAYS * MS_PER_DAY;
}

export function NewBadge({ date, text, placement = 'top-end', variant = 'success' }: NewBadgeProps) {
    if (!isVisible(date)) {
        return null;
    }

    const { vars, inner } = variantConfig[variant];

    return (
        <Tooltip
            placement={placement}
            tooltip={<p className="tw-my-2 tw-max-w-[220px] tw-text-xs tw-text-accents-7">{text}</p>}
            className="tw-inline-flex"
            withArrow
        >
            <span className={`tw-pointer-events-none tw-relative tw-inline-flex tw-select-none ${vars}`}>
                {/* Soft pulsing halo behind the badge */}
                <span
                    aria-hidden
                    className="tw-absolute tw-inset-0 tw-rounded-full tw-bg-[var(--nb-soft)] tw-blur-md tw-animate-new-badge-glow-pulse motion-reduce:tw-hidden"
                />

                {/* Glowing border – static ring with a highlight running around it */}
                <span className="tw-relative tw-inline-flex tw-overflow-hidden tw-rounded-full tw-bg-[var(--nb-glow)] tw-p-px tw-shadow-[0_0_8px_var(--nb-soft)]">
                    <span
                        aria-hidden
                        className="tw-absolute tw-left-1/2 tw-top-1/2 tw-h-[180%] tw-w-[180%] tw--translate-x-1/2 tw--translate-y-1/2 tw-animate-new-badge-spin tw-bg-[conic-gradient(from_0deg,transparent_0deg,var(--nb-glow)_60deg,var(--nb-bright)_95deg,var(--nb-glow)_130deg,transparent_200deg,transparent_360deg)] motion-reduce:tw-hidden"
                    />

                    {/* Inner pill */}
                    <span
                        className={`tw-relative tw-z-10 tw-inline-flex tw-items-center tw-gap-1 tw-overflow-hidden tw-rounded-full tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-uppercase tw-leading-[13px] tw-tracking-wide ${inner}`}
                    >
                        <FontAwesomeIcon icon={faStar} className="tw-h-2 tw-w-2 tw-opacity-90" />
                        Neu
                        {/* Diagonal shimmer sweep */}
                        <span
                            aria-hidden
                            className="tw-absolute tw-inset-y-0 tw-left-0 tw-w-1/3 tw-bg-white/40 tw-blur-[2px] tw-animate-new-badge-shimmer motion-reduce:tw-hidden"
                        />
                    </span>
                </span>
            </span>
        </Tooltip>
    );
}
